import React, { useEffect, useState } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";
import { useLoader } from "../../../utils/LoaderUtils";
import useNumericInput from "../../../hooks/useNumericInput";
import SubmitButton from "../../../components/partials/global/SubmitButton";
import warehouseAPI from "../../../api/inventory/warehouseApi";
import GlobalDeleteModal from "../../../components/partials/GlobalDeleteModal";

function WarehouseSettings() {
  useNumericInput();

  const [addresses, setAddresses] = useState([]);
  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [editId, setEditId] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const INITIAL_FORM_DATA = {
    wareHouseId: "",
    wareHouseName: "",
    email: "",
    mobile: "",
    addressLine1: "",
    area: "",
    state: "",
    country: "",
    pincode: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "pincode") {
      handlePincodeChange(value);
    }
  };

  /******* get form data****** */
  useEffect(() => {
    fetchWarehouseData();
  }, []);

  const fetchWarehouseData = async () => {
    try {
      const res = await warehouseAPI.list();

      if (res.code === 200) {
        const result = res.data;
        setAddresses(result.warehouses);
      }
    } catch (err) {}
  };

  /********* Validation and form submission *********** */

  const validate = () => {
    const newErrors = {};

    if (!formData.wareHouseName.trim()) {
      newErrors.wareHouseName = "WareHouse Id field is mandatory";
    }

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = "Address field is mandatory";
    }

    if (!formData.area.trim()) {
      newErrors.area = "Area field is mandatory";
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "PinCode field is mandatory";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    showLoader();

    if (!validate()) {
      setIsLoading(false);
      hideLoader();
      return;
    }

    try {
      const payload = {
        ...formData,
        id: editId,
      };

      const res = await warehouseAPI.addDetails(payload);
      if (res.code === 200) {
        setFormData(INITIAL_FORM_DATA);
        setEditId(null);
        setShowForm(false);
        fetchWarehouseData();
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  const handleEdit = (item) => {
    setFormData({
      wareHouseId: item.wareHouseId,
      wareHouseName: item.wareHouseName,
      email: item.email,
      mobile: item.mobile,
      addressLine1: item.addressLine1,
      area: item.area,
      state: item.state,
      country: item.country,
      pincode: item.pincode,
      city: item.city,
    });

    setEditId(item._id); // ⚠️ backend id
    setShowForm(true);
  };

  /************* delete oeprations *********/
  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);

      const res = await warehouseAPI.deleteItem(deleteId);

      if (res.code === 200) {
        showSuccess(res.message);

        setAddresses((prev) => prev.filter((addr) => addr._id !== deleteId));
        document.querySelector("#delete-modal .btn-close").click();
      } else {
        showError(res.message);
      }
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  /************* pincode data fetching *********/
  const handlePincodeChange = async (value) => {
    setFormData((prev) => ({ ...prev, pincode: value }));

    if (value.length === 6) {
      const res = await fetch(`https://api.postalpincode.in/pincode/${value}`);
      const data = await res.json();

      if (data[0].Status === "Success") {
        setFormData((prev) => ({
          ...prev,
          state: data[0].PostOffice[0].State,
          city: data[0].PostOffice[0].District,
          country: data[0].PostOffice[0].Country,
        }));
      } else {
        showError("Invalid Pincode");
      }
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h4 className="mb-0">PickUp Addresses</h4>

          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? "Cancel" : "+ Add Address"}
          </button>
        </div>

        <div className="card-body">
          {showForm && (
            <div className="border rounded p-3 mb-3">
              <form onSubmit={handleSubmit}>
                <div className="row g-2">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      WareHouse Id <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="wareHouseId"
                      className="form-control"
                      placeholder="WareHouse Id"
                      value={formData.wareHouseId}
                      onChange={handleChange}
                    />
                    {errors.wareHouseId && (
                      <span className="errors text-danger">
                        {errors.wareHouseId}
                      </span>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      WareHouse Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="wareHouseName"
                      className="form-control"
                      placeholder="WareHouse Name"
                      value={formData.wareHouseName}
                      onChange={handleChange}
                    />
                    {errors.wareHouseName && (
                      <span className="errors text-danger">
                        {errors.wareHouseName}
                      </span>
                    )}
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Mobile <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="mobile"
                      className="form-control only-numeric"
                      placeholder="Mobile"
                      maxLength={10}
                      value={formData.mobile}
                      onChange={handleChange}
                    />
                    {errors.mobile && (
                      <span className="errors text-danger">
                        {errors.mobile}
                      </span>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email && (
                      <span className="errors text-danger">{errors.email}</span>
                    )}
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Street Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      className="form-control"
                      placeholder="Address"
                      value={formData.addressLine1}
                      onChange={handleChange}
                    />
                    {errors.addressLine1 && (
                      <span className="errors text-danger">
                        {errors.addressLine1}
                      </span>
                    )}
                  </div>
                </div>

                <div className="row g-2">
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      Area <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="area"
                      className="form-control"
                      placeholder="Area"
                      value={formData.area}
                      onChange={handleChange}
                    />
                    {errors.area && (
                      <span className="errors text-danger">{errors.area}</span>
                    )}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      PinCode <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="pincode"
                      className="form-control only-numeric"
                      placeholder="Pincode"
                      maxLength={6}
                      value={formData.pincode}
                      onChange={handleChange}
                    />
                    {errors.pincode && (
                      <span className="errors text-danger">
                        {errors.pincode}
                      </span>
                    )}
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      className="form-control"
                      placeholder="City"
                      value={formData.city}
                      onChange={handleChange}
                    />
                    {errors.city && (
                      <span className="errors text-danger">{errors.city}</span>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="state"
                      className="form-control"
                      placeholder="State"
                      value={formData.state}
                      onChange={handleChange}
                    />
                    {errors.state && (
                      <span className="errors text-danger">{errors.state}</span>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Country <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="country"
                      className="form-control"
                      placeholder="Country"
                      value={formData.country}
                      onChange={handleChange}
                    />
                    {errors.country && (
                      <span className="errors text-danger">
                        {errors.country}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-3 text-end">
                  <SubmitButton
                    isLoading={isLoading}
                    text={editId ? "Update Address" : "Save Address"}
                    loadingText={editId ? "Updating..." : "Saving..."}
                  />
                </div>
              </form>
            </div>
          )}

          {/* ✅ Address List */}
          {addresses.length === 0 ? (
            <div className="text-center text-muted py-4">
              No pickup addresses added yet
            </div>
          ) : (
            addresses.map((item) => (
              <div
                key={item._id}
                className="border rounded p-3 mb-2 d-flex justify-content-between align-items-start"
              >
                <div>
                  <div className="fw-bold">
                    {item.wareHouseName} ({item.wareHouseId})
                  </div>
                  <div className="text-muted small">
                    {item.mobile}, {item.email}
                  </div>
                  <div className="small">
                    {item.addressLine1}, {item.area}, {item.city}, {item.state},
                    - {item.pincode}, {item.country}
                  </div>
                </div>

                <div>
                  <button
                    className="btn btn-sm btn-outline-warning me-2"
                    onClick={() => handleEdit(item)}
                  >
                    <i className="ti ti-pencil" />
                  </button>

                  <a
                    className="btn btn-sm btn-outline-danger"
                    href="javascript:void(0);"
                    data-bs-toggle="modal"
                    data-bs-target="#delete-modal"
                    onClick={(e) => {
                      handleOpenDeleteModal(item._id);
                    }}
                  >
                    <i className="ti ti-trash" />
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default WarehouseSettings;
